import "./claim-item.scss";

import { Constraint } from "@core/model/constraint.enum";
import { useUserIdentityContext } from "@core/services/user-identity.context";
import GridItem from "@core/ui/grid-item/grid-item";
import { ActionEnum } from "@factory/model/actions.enum";
import { GetCampaignModel } from "@factory/model/query-model-campaign";
import { GetCouponModel, GetResponseCouponModel } from "@factory/model/query-model-coupon";
import {
  GetResponseRequestModel,
  MutateResponseRequestModel,
  RequestCreateData,
} from "@factory/model/query-model-request";
import { useQyGetCoupon } from "@factory/query/coupon.query";
import {
  useQyCreateRequest,
  useQyGetRequest,
} from "@factory/query/request.query";
import { useFormApiMapper } from "@factory/services/form-api-mapper.hook";
import { useNotificationContext } from "@shared/ui/notification/notification.context";
import { useDateTimeContext } from "@shared/utility/date-time-service/date-time.context";
import { toBoolean } from "@shared/utility/to-boolean";
import { differenceInSeconds } from "date-fns";
import { isNil } from "lodash-es";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { FloatLabel } from "primereact/floatlabel";
import { InputText } from "primereact/inputtext";
import { useCallback, useState } from "react";
import QRCode from "react-qr-code";

import { appFeature } from "../../../../app.feature";
import { useListCardsContext } from "../list-cards.context";

export interface ClaimItemProps {}

export function ClaimItem() {
  // DECLARATIONS
  const { pointsText, stampsText } = appFeature.config.label;
  const { previewRedeem, selectedCard, tenant, balanceData, navigate } =
    useListCardsContext();
  const { showInfo } = useNotificationContext();
  const { getClientFeatures } = useUserIdentityContext();
  const { formatDateTimeLocal, formatToLocale } = useDateTimeContext();
  const { mapCreateRequest } = useFormApiMapper();
  const [requestRedeemData, setRequestRedeemData] =
    useState<RequestCreateData | null>(null);
  const [redeemEnabled, setRedeemEnabled] = useState(true);
  const previewRedeemLevel = (previewRedeem?.metadata as GetCampaignModel)?.level_name;
  const clientFeatures = getClientFeatures();
  const [couponCode, setCouponCode] = useState("")
  const [couponData, setCouponData] = useState({
    code: "",
    valueType: "",
    points: 0,
    stamps: 0,
  })

  // API DEFINITIONS
  const handleGetRequestSuccess = (response: GetResponseRequestModel) => {
    if (!response || response.data?.length === 0) {
      return;
    }

    const responseData = response.data?.[0] as unknown as RequestCreateData;
    const expirationDate = formatToLocale(responseData.expiration);
    const now = new Date();
    const diffInSec = differenceInSeconds(expirationDate, now);

    if (diffInSec > 0 && responseData.status === "PENDING") {
      setRequestRedeemData(responseData);
      setRedeemEnabled(false);
    }
    // eslint-disable-next-line no-console
    console.log({ responseData, expirationDate, now, diffInSec });
  };
  useQyGetRequest(
    "",
    99,
    0,
    undefined,
    {
      user_code: selectedCard?.user_code ?? "",
      campaign: previewRedeem?.code ?? "",
    },
    !!selectedCard?.user_code && !!previewRedeem?.code,
    handleGetRequestSuccess
  );

  const handleRequestRedeemSuccess = (response: MutateResponseRequestModel) => {
    const data = response.data as RequestCreateData;
    setRequestRedeemData(data);
  };
  const { mutate: requestRedeem } = useQyCreateRequest(
    handleRequestRedeemSuccess
  );

  const handleCouponDataSuccess = (response: GetResponseCouponModel) => {
    const data = response.data;
    if (data && data.length > 0) {
      setCouponData({
        code: data[0].code || "",
        valueType: data[0].value_type || "",
        points: data[0].points || 0,
        stamps: data[0].stamps || 0,
      });
    }
    else {
      showInfo("Invalid coupon");
      setCouponData({
        code: "",
        valueType: "", 
        points: 0, 
        stamps: 0
      })
    }
  }
  useQyGetCoupon(
    "", 
    1,
    0, 
    undefined, 
    {coupon_code: couponCode, purpose: ActionEnum.Redeem},
    (couponCode !== "" && couponCode.length >= 5) ? true : false, 
    handleCouponDataSuccess
  );

  // LOCAL FUNCTIONS
  const isRedeemable = useCallback(() => {
    if (isNil(previewRedeem) || isNil(balanceData)) {
      return;
    }

    const campaignData = previewRedeem?.metadata;
    if (
      campaignData &&
      "is_redeemable" in campaignData &&
      !toBoolean(campaignData.is_redeemable as string)
    ) {
      return {
        allow: false,
        description: `Your level is not enough to redeem this campaign`,
      };
    }

    if ((previewRedeem.limitPerMonth > 0) && (previewRedeem?.monthlyRedeemCount >= previewRedeem.limitPerMonth)) {
      return {
        allow: false,
        description: `You have reached the monthly redeem limit for this item.`
      }
    }

    if (previewRedeem?.constraint === Constraint.AND) {
      let description = "";
      const allowRedeem =
        balanceData?.current_points >= previewRedeem?.points &&
        balanceData?.current_stamp >= previewRedeem?.stamp;

      if (!allowRedeem) {
        const neededPoints = previewRedeem.points - balanceData.current_points;
        const neededStamp = previewRedeem.stamp - balanceData.current_stamp;
        const neededPointsText =
          neededPoints > 0 ? `${neededPoints} ${pointsText} more needed.` : "";
        const neededStampText =
          neededStamp > 0 ? `${neededStamp} STAMP more needed.` : "";
        description = `Insufficient ${pointsText} AND ${stampsText}. ${neededPointsText} ${neededStampText}`;
      }

      return {
        allow: allowRedeem,
        description,
      };
    }

    if (previewRedeem?.constraint === Constraint.OR) {
      let description = "";
      const _allowedPoints =
        balanceData?.current_points >= previewRedeem?.points;
      const _allowedStamp =
        previewRedeem.stamp <= 0
          ? false
          : balanceData?.current_stamp >= previewRedeem.stamp;
      const allowRedeem = _allowedPoints || _allowedStamp;

      if (!allowRedeem) {
        const neededPoints = previewRedeem.points - balanceData.current_points;
        const neededStamp = previewRedeem.stamp - balanceData.current_stamp;
        const neededPointsText =
          neededPoints > 0 ? `${neededPoints} ${pointsText} more needed.` : "";
        const neededStampText =
          neededStamp > 0 ? `${neededStamp} ${stampsText} more needed.` : "";
        description = `Insufficient ${pointsText} OR ${stampsText}. ${neededPointsText} ${neededStampText} `;
      }

      return {
        allow: allowRedeem,
        description,
      };
    }

    return {
      allow: false,
      description: "Campaign not available at the moment",
    };
  }, [balanceData?.code, previewRedeem?.code]);

  const handleRequestRedeem = () => {
    if (!previewRedeem || !selectedCard) {
      return;
    }

    const payload = mapCreateRequest({
      campaign: previewRedeem.code,
      cardSeries: selectedCard?.card_alias,
      tenant: tenant,
      userCode: selectedCard?.user_code,
      coupon: couponData?.code
    });
    requestRedeem(payload);
  };

  // LOCAL RENDERS
  const redeemHeaderElement = (
    <div className="flex flex-col items-center justify-center gap-2">
      <span className="font-bold white-space-nowrap">Claim Reward</span>
    </div>
  );
  const redeemFooterContent = (
    <div className="flex flex-col gap-2">
      {isRedeemable()?.allow ? (
        <> 
          {clientFeatures?.featureCoupon ? (
            <div className="mb-6">
              <FloatLabel>
                <InputText
                  id="couponCode"
                  value={couponCode}
                  onChange={(e) => {
                    setCouponCode(e.target.value); 
                    if (e.target.value === "") setCouponData({code: "", valueType: "", points: 0, stamps: 0});
                  }}
                  className="text-4xl w-full"
                />
                <label htmlFor="couponCode">Enter Coupon Code (Optional)</label>
              </FloatLabel>
            </div>
          ) : null}
          <Button
            label="Redeem"
            disabled={!redeemEnabled}
            autoFocus
            onClick={() => handleRequestRedeem()}
          />
        </>
      ) : (
        <Button
          disabled={true}
          label={isRedeemable()?.description}
          severity="secondary"
        />
      )}
      <Button
        label="Close"
        outlined
        severity="secondary"
        onClick={() => navigate("/customer")}
      />
    </div>
  );

  return (
    <div id="ClaimItem" className="claim-item">
      <Dialog
        header={redeemHeaderElement}
        footer={redeemFooterContent}
        visible={true}
        maximized={true}
        onHide={() => {
          navigate("/customer");
        }}
        closable={false}
      >
        <div className="m-0">
          {previewRedeem ? (
            <>
              <GridItem campaign={previewRedeem} couponData={couponCode ? couponData : undefined} />
              {previewRedeemLevel ? (
                <div>
                  This item is only eligible for{" "}
                  <span className="font-bold">
                    {(previewRedeem.metadata as GetCampaignModel).level_name}
                  </span>{" "}
                  level.
                </div>
              ) : null}
            </>
          ) : (
            <p className="text-center">No reward selected. Return to list</p>
          )}
          {requestRedeemData ? (
            <>
              <div className="flex justify-center items-center my-8">
                <QRCode value={requestRedeemData?.code} size={250} />
              </div>
              <p>
                Present this QR code to the cashier for redemption. This is only
                available until{" "}
                <b>{formatDateTimeLocal(requestRedeemData.expiration)}</b>
              </p>
            </>
          ) : null}
          {previewRedeem && !requestRedeemData ? (
            <p className="mt-6">
              To claim this reward, <b>Press the Redeem button</b> to generate a
              QR Code for you to present to the cashier
            </p>
          ) : null}
        </div>
      </Dialog>
    </div>
  );
}

export default ClaimItem;
